import { useCallback, useRef, useState } from "react";
import type { BrandSpec } from "../../../types";
import { extractBrand, type ExtractBrandOptions } from "../services/brandExtractor";
import { getActivePresentationBuffer } from "../../../utils/officePptx";
import { fetchSavedBrandTemplateBytes } from "../services/brandTemplatesApi";
import { logError, scrubMessage } from "../../../../../telemetry";

export type ExtractorState =
  | { status: "idle" }
  | { status: "extracting"; filename: string }
  | { status: "done"; brand: BrandSpec; filename: string }
  | { status: "error"; message: string };

/** Display label for the "extract from the open deck" source — used for both
 * the "extracting…" and "done" states so they can't drift apart. */
const ACTIVE_PRESENTATION_LABEL = "Active Presentation";

/** connectorSystemName tag for Deckorate apply-brand telemetry. */
const APPLY_BRAND_CONNECTOR = "deckorate";

/** Report an apply-brand failure through the shared telemetry pipeline (PII-scrubbed). */
function logApplyBrandError(reason: string, type: string, message: string): void {
  logError(
    "error.connector.failure",
    { type, scrubbedMessage: scrubMessage(message) },
    { connectorSystemName: APPLY_BRAND_CONNECTOR, reason },
  );
}

export function useBrandExtractor() {
  const [state, setState] = useState<ExtractorState>({ status: "idle" });
  // Guards against overlapping extractions (e.g. a double-click before the
  // "extracting" state re-renders and disables the trigger button).
  const isExtractingRef = useRef(false);

  const extract = useCallback(async (file: File, options?: ExtractBrandOptions) => {
    if (isExtractingRef.current) return;
    isExtractingRef.current = true;
    setState({ status: "extracting", filename: file.name });
    try {
      const buffer = await file.arrayBuffer();
      const brand = await extractBrand(buffer, options);
      setState({ status: "done", brand, filename: file.name });
    } catch (e) {
      const message = e instanceof Error ? e.message : "Extraction failed";
      logApplyBrandError("brand-extract-file", "ExtractionError", message);
      setState({ status: "error", message });
    } finally {
      isExtractingRef.current = false;
    }
  }, []);

  const extractFromActive = useCallback(async (options?: ExtractBrandOptions) => {
    if (isExtractingRef.current) return;
    isExtractingRef.current = true;
    setState({ status: "extracting", filename: ACTIVE_PRESENTATION_LABEL });
    try {
      const buffer = await getActivePresentationBuffer();
      const brand = await extractBrand(buffer, options);
      setState({ status: "done", brand, filename: ACTIVE_PRESENTATION_LABEL });
    } catch (e) {
      const message = e instanceof Error ? e.message : "Extraction failed";
      logApplyBrandError("brand-extract-active", "ExtractionError", message);
      setState({ status: "error", message });
    } finally {
      isExtractingRef.current = false;
    }
  }, []);

  /** Extracts from bytes already in memory (an upload being re-selected, or a
   *  different master of the same file) — no re-reading of the source. */
  const extractFromBuffer = useCallback(async (buffer: ArrayBuffer, filename: string, options?: ExtractBrandOptions) => {
    if (isExtractingRef.current) return;
    isExtractingRef.current = true;
    setState({ status: "extracting", filename });
    try {
      const brand = await extractBrand(buffer, options);
      setState({ status: "done", brand, filename });
    } catch (e) {
      const message = e instanceof Error ? e.message : "Extraction failed";
      logApplyBrandError("brand-extract-buffer", "ExtractionError", message);
      setState({ status: "error", message });
    } finally {
      isExtractingRef.current = false;
    }
  }, []);

  /** Same flow as `extract`, but downloads bytes from a Saved Templates row's
   *  `sourceFileUrlCandidates` (see fetchSavedBrandTemplateBytes) instead of
   *  reading a local `File`. */
  const extractFromUrls = useCallback(async (urls: readonly string[], filename: string) => {
    if (isExtractingRef.current) return;
    isExtractingRef.current = true;
    setState({ status: "extracting", filename });
    try {
      const buffer = await fetchSavedBrandTemplateBytes(urls);
      const brand = await extractBrand(buffer);
      setState({ status: "done", brand, filename });
    } catch (e) {
      const message = e instanceof Error ? e.message : "Extraction failed";
      logApplyBrandError("brand-extract-saved", "ExtractionError", message);
      setState({ status: "error", message });
    } finally {
      isExtractingRef.current = false;
    }
  }, []);

  /** Sets an already-built BrandSpec as "done" — no download/parsing. Used
   *  when a Saved Templates row's `themeColors` already gives us enough to
   *  build a BrandSpec synchronously (buildBrandSpecFromThemeColors). */
  const setDirectBrand = useCallback((brand: BrandSpec, filename: string) => {
    setState({ status: "done", brand, filename });
  }, []);

  const reset = useCallback(() => setState({ status: "idle" }), []);

  return { state, extract, extractFromActive, extractFromBuffer, extractFromUrls, setDirectBrand, reset };
}
